import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Shield, Eye, EyeOff, Copy, ExternalLink, Loader2, Clock, AlertTriangle, Mail, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";

const API = "";

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert`);
  } catch {
    toast.error("Kopieren fehlgeschlagen");
  }
}

export default function SharedVaultPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/vault/shared/${token}`);
        if (res.ok) {
          setData(await res.json());
        } else {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || "Link ungueltig oder abgelaufen");
        }
      } catch {
        setError("Netzwerkfehler");
      }
      setLoading(false);
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Zugriff nicht moeglich</h1>
          <p className="text-sm text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1117] flex items-center justify-center px-4 py-8" data-testid="shared-vault-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-amber-500/15 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Shield className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Geteilte Zugangsdaten</h1>
          <p className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" />
            Gueltig bis {new Date(data.expires_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} Uhr
          </p>
          {data.shared_by && (
            <p className="text-xs text-gray-600 mt-0.5">Geteilt von {data.shared_by}</p>
          )}
        </div>

        {/* Card */}
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden">
          {/* Title */}
          <div className="px-5 py-4 border-b border-white/8">
            <p className="text-lg font-bold text-white" data-testid="shared-vault-title">{data.title}</p>
            {data.category && <p className="text-xs text-gray-500 mt-0.5 uppercase">{data.category}</p>}
          </div>

          {/* Username */}
          {data.username && (
            <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between" data-testid="shared-vault-username">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Benutzername</p>
                <p className="text-sm text-white font-mono">{data.username}</p>
              </div>
              <button onClick={() => copyToClipboard(data.username, "Benutzername")}
                className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors"
                data-testid="shared-copy-username"
              >
                <Copy className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          )}

          {/* Password */}
          <div className="px-5 py-4 border-b border-white/8" data-testid="shared-vault-password">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Passwort</p>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-white font-mono flex-1 truncate">
                {showPw ? data.password : "••••••••••••"}
              </p>
              <div className="flex gap-1.5">
                <button onClick={() => setShowPw(!showPw)}
                  className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors"
                  data-testid="shared-toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                </button>
                <button onClick={() => copyToClipboard(data.password, "Passwort")}
                  className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors"
                  data-testid="shared-copy-password"
                >
                  <Copy className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>

          {/* URL */}
          {data.url && (
            <div className="px-5 py-4 flex items-center justify-between" data-testid="shared-vault-url">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">URL</p>
                <p className="text-sm text-blue-400 truncate">{data.url}</p>
              </div>
              <a href={data.url} target="_blank" rel="noopener noreferrer"
                className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors ml-2"
                data-testid="shared-open-url"
              >
                <ExternalLink className="w-4 h-4 text-blue-400" />
              </a>
            </div>
          )}

          {/* E-Mail Konto Details — nur fuer entry_type == email_account */}
          {data.entry_type === "email_account" && (
            <SharedEmailAccountBlock data={data} />
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-gray-700 mt-6">TGAone Passwort-Tresor</p>
      </div>
    </div>
  );
}

// ─── E-Mail Konto Details (Shared View) ───
function SharedEmailAccountBlock({ data }) {
  const securityLabel = (s) => {
    const v = (s || "").toUpperCase();
    if (v === "SSL_TLS" || v === "SSL" || v === "TLS") return "SSL/TLS";
    if (v === "STARTTLS") return "STARTTLS";
    if (v === "NONE" || v === "PLAIN") return "Keine";
    return s || "—";
  };
  const rows = [
    { key: "email_address", label: "E-Mail Adresse", value: data.email_address, mono: true },
    { key: "imap_host",     label: "IMAP Server",    value: data.imap_host, mono: true },
    { key: "imap_port",     label: "IMAP Port",      value: data.imap_port ? String(data.imap_port) : "", mono: true },
    { key: "imap_security", label: "IMAP Sicherheit", value: securityLabel(data.imap_security), mono: false },
    { key: "smtp_host",     label: "SMTP Server",    value: data.smtp_host, mono: true },
    { key: "smtp_port",     label: "SMTP Port",      value: data.smtp_port ? String(data.smtp_port) : "", mono: true },
    { key: "smtp_security", label: "SMTP Sicherheit", value: securityLabel(data.smtp_security), mono: false },
  ].filter(r => !!r.value);

  if (rows.length === 0) return null;

  const copyAll = async () => {
    const lines = [
      `Konto: ${data.title}`,
      ...rows.map(r => `${r.label}: ${r.value}`),
      `Benutzername: ${data.username || data.email_address || ""}`,
      `Passwort: ${data.password}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Alle E-Mail-Daten kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  return (
    <div className="px-5 py-4 border-t border-blue-500/20 bg-blue-500/[0.03]" data-testid="shared-email-block">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />E-Mail Konto Details
        </p>
        <button onClick={copyAll}
          className="h-8 px-3 rounded-lg text-[11px] font-bold bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-300 flex items-center gap-1.5"
          data-testid="shared-email-copy-all"
        >
          <ClipboardCopy className="w-3 h-3" />
          Alle Daten kopieren
        </button>
      </div>
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.key} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.02] border border-white/5"
            data-testid={`shared-email-row-${r.key}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold leading-none mb-0.5">{r.label}</p>
              <p className={`text-[12px] text-white truncate ${r.mono ? "font-mono" : ""}`}>{r.value}</p>
            </div>
            <button onClick={() => copyToClipboard(r.value, r.label)}
              className="p-1.5 bg-white/[0.06] rounded-md active:bg-white/15 transition-colors flex-shrink-0"
              data-testid={`shared-email-copy-${r.key}`}
              title={`${r.label} kopieren`}>
              <Copy className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-500 mt-2">
        Tipp: „Alle Daten kopieren" enthaelt auch Benutzername und Passwort fuer Outlook.
      </p>
    </div>
  );
}
